<template>
  <div>
    <div class="d-flex flex-wrap align-center mb-4" style="gap: 12px">
      <v-select
        v-model="period"
        :items="periods"
        label="Период"
        outlined dense hide-details
        style="max-width: 220px"
      />
      <v-select
        v-model="status"
        :items="statuses"
        label="Статус"
        outlined dense hide-details clearable
        style="max-width: 200px"
      />
    </div>

    <StateBlock v-if="loading && !orders.length" state="loading" />
    <StateBlock v-else-if="error && error.status === 403" state="forbidden" />
    <StateBlock v-else-if="error" state="error" @retry="load" />
    <StateBlock v-else-if="!orders.length" state="empty" title="Заказов пока нет" :text="emptyText">
      <v-btn v-if="!isAdminMode" color="primary" outlined small :to="{ name: 'list-services' }">В каталог</v-btn>
    </StateBlock>

    <v-data-table
      v-else
      :headers="headers"
      :items="orders"
      :loading="loading"
      :server-items-length="count"
      :options.sync="options"
      :footer-props="{ 'items-per-page-options': [10, 25, 50], 'items-per-page-text': 'Заказов на странице:' }"
      item-key="id"
      show-expand
      single-expand
      disable-sort
      @item-expanded="onExpand"
    >
      <template v-slot:item.created_at="{ item }">{{ formatDay(item.created_at) }}</template>
      <template v-slot:item.id="{ item }">№{{ item.id }}</template>
      <template v-slot:item.user="{ item }">
        <div>{{ customerName(item) }}</div>
        <div v-if="item.user_email && item.user_email !== customerName(item)" class="caption grey--text">{{ item.user_email }}</div>
      </template>
      <template v-slot:item.role="{ item }">{{ roleTitle(item.ordering_role) }}</template>
      <template v-slot:item.items="{ item }">
        <span v-if="details[item.id]">{{ itemsSummary(details[item.id]) }}</span>
        <span v-else class="grey--text">{{ item.items_count || 0 }} поз. · {{ item.items_quantity || 0 }} шт.</span>
      </template>
      <template v-slot:item.status="{ item }">
        <v-chip small :color="statusColor(item.status)" text-color="white">{{ item.status_display || statusTitle(item.status) }}</v-chip>
      </template>
      <template v-slot:item.actions="{ item }">
        <div v-if="isAdminMode && item.status === 'pending'" class="d-flex justify-end" style="gap: 8px">
          <v-btn x-small color="success" @click.stop="askAction(item, 'confirm')">Оплачен</v-btn>
          <v-btn x-small outlined color="error" @click.stop="askAction(item, 'cancel')">Отменить</v-btn>
        </div>
      </template>

      <template v-slot:expanded-item="{ headers: cols, item }">
        <td :colspan="cols.length" class="py-3">
          <div v-if="detailsLoading[item.id]" class="caption grey--text">Загрузка состава…</div>
          <div v-else-if="!details[item.id]" class="caption error--text">Не удалось загрузить состав заказа</div>
          <template v-else>
            <v-simple-table dense style="max-width: 620px">
              <thead><tr><th>Услуга</th><th class="text-right">Кол-во</th><th class="text-right">Сумма</th><th>Зачисление</th></tr></thead>
              <tbody>
                <tr v-for="row in details[item.id].items || []" :key="row.id">
                  <td>{{ row.service_name }}</td>
                  <td class="text-right">{{ row.quantity }}</td>
                  <td class="text-right">{{ row.total_price }} ₽</td>
                  <td>{{ row.replenish_buffer ? 'В буфер компании' : '—' }}</td>
                </tr>
              </tbody>
            </v-simple-table>
            <div class="caption mt-2">{{ resultText(details[item.id]) }}</div>
            <div v-if="details[item.id].notes" class="caption grey--text mt-1">Комментарий: {{ details[item.id].notes }}</div>
          </template>
        </td>
      </template>
    </v-data-table>

    <v-dialog v-model="confirmDialog.show" max-width="440px">
      <v-card class="pa-6">
        <h3 class="mb-3">{{ confirmDialog.action === 'confirm' ? 'Отметить заказ оплаченным?' : 'Отменить заказ?' }}</h3>
        <div class="mb-2">Заказ №{{ confirmDialog.order && confirmDialog.order.id }} · {{ confirmDialog.order && customerName(confirmDialog.order) }}</div>
        <div class="caption grey--text text--darken-1">
          {{ confirmText }}
        </div>
        <v-alert v-if="confirmDialog.error" type="error" dense outlined class="mt-3 mb-0">{{ confirmDialog.error }}</v-alert>
        <div class="d-flex justify-end mt-5" style="gap: 12px">
          <v-btn text :disabled="confirmDialog.saving" @click="confirmDialog.show = false">Назад</v-btn>
          <v-btn :color="confirmDialog.action === 'confirm' ? 'success' : 'error'" :loading="confirmDialog.saving" @click="runAction">
            {{ confirmDialog.action === 'confirm' ? 'Оплачен' : 'Отменить заказ' }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from 'moment';
import api from '@/common/api';
import StateBlock from '@/components/ui/StateBlock.vue';
import { WALLET_PERIODS, walletPeriodRange } from '@/components/clients/card/walletPeriods';
import {
  ADMIN, MANAGER, CORPCLIENT, CANDIDATE, PARTNER,
} from '@/common/constants/userTypes';

// Роль, от имени которой оформлен заказ: ordering_role = { type, id }, у старых заказов null.
// Неизвестный тип показываем как есть.
const ROLES = {
  [ADMIN]: 'Администратор',
  [MANAGER]: 'Менеджер',
  [CORPCLIENT]: 'Корп. клиент',
  [CANDIDATE]: 'Кандидат',
  [PARTNER]: 'Партнёр',
};

const STATUS = {
  pending: { title: 'Ожидает оплаты', color: 'orange' },
  paid: { title: 'Оплачен', color: 'green' },
  cancelled: { title: 'Отменён', color: 'grey' },
  refunded: { title: 'Возврат', color: 'blue-grey' },
};

// Заказы (Э-8): «Мои заказы» корпа, «Заказы» админа и вкладка «Заказы» в карточке корпа.
export default {
  name: 'OrdersTable',
  components: { StateBlock },
  props: {
    // my — свои заказы; admin — все заказы с действиями; corp — заказы одного корпа (по email) с действиями
    mode: { type: String, default: 'my', validator: (v) => ['my', 'admin', 'corp'].includes(v) },
    email: { type: String, default: '' },
  },
  data: () => ({
    orders: [],
    count: 0,
    loading: false,
    error: null,
    options: { page: 1, itemsPerPage: 25 },
    period: 'all',
    status: null,
    periods: WALLET_PERIODS,
    statuses: Object.entries(STATUS).map(([value, s]) => ({ value, text: s.title })),
    details: {},
    detailsLoading: {},
    confirmDialog: {
      show: false, action: 'confirm', order: null, saving: false, error: '',
    },
    requestId: 0,
  }),
  computed: {
    isAdminMode() {
      return this.mode !== 'my';
    },
    confirmText() {
      const { action, order } = this.confirmDialog;
      if (action !== 'confirm') return 'Статус сменится на «Отменён». Действие нельзя отменить.';
      const detail = order ? this.details[order.id] : null;
      const base = 'Статус сменится на «Оплачен».';
      // Состав может быть ещё не загружен: тогда не обещаем зачисление
      if (!detail || !detail.items) return `${base} Начислит ли оплата тесты в буфер, определяется составом заказа — проверьте остатки на «Обзоре» корпа.`;
      if (!detail.items.some((i) => i.replenish_buffer)) return `${base} В заказе нет позиций с зачислением в буфер.`;
      return `${base} Тесты из позиций с зачислением попадут в буфер компании и спишутся с кошельков администратора, который подтверждает. Повторное подтверждение ничего не начислит. Если на кошельках админа не хватает, сервер вернёт ошибку и заказ не изменится.`;
    },
    emptyText() {
      if (this.mode === 'corp' && !this.email) return 'У корп. клиента не указан email — заказы найти не по чему.';
      return this.mode === 'my' ? 'Оформите заказ в каталоге услуг — он появится здесь.' : '';
    },
    headers() {
      return [
        { text: 'Дата', value: 'created_at', width: '110px' },
        { text: 'Номер', value: 'id', width: '90px' },
        ...(this.mode === 'admin' ? [{ text: 'Кто оформил', value: 'user' }, { text: 'Роль', value: 'role' }] : []),
        { text: 'Состав', value: 'items' },
        { text: 'Сумма', value: 'total_amount', align: 'end' },
        { text: 'Статус', value: 'status' },
        ...(this.isAdminMode ? [{ text: '', value: 'actions', align: 'end' }] : []),
        { text: '', value: 'data-table-expand' },
      ];
    },
  },
  watch: {
    options: {
      handler(val, old) {
        if (!old || val.page !== old.page || val.itemsPerPage !== old.itemsPerPage) this.load();
      },
      deep: true,
    },
    period() {
      this.resetAndLoad();
    },
    status() {
      this.resetAndLoad();
    },
    email() {
      this.resetAndLoad();
    },
  },
  methods: {
    resetAndLoad() {
      if (this.options.page !== 1) this.options = { ...this.options, page: 1 };
      else this.load();
    },
    async load() {
      if (this.mode === 'corp' && !this.email) {
        this.orders = [];
        this.count = 0;
        this.error = null;
        this.loading = false;
        return;
      }
      this.requestId += 1;
      const { requestId } = this;
      this.loading = true;
      this.error = null;
      const { from, to } = walletPeriodRange(this.period);
      const params = {
        page: this.options.page,
        page_size: this.options.itemsPerPage,
        ordering: '-created_at',
      };
      if (from) params.from_date = from;
      if (to) params.to_date = to;
      if (this.status) params.status = this.status;
      if (this.mode === 'corp' && this.email) params.email = this.email;
      try {
        const response = await api.get('/service-catalog/purchases/', { params });
        if (requestId !== this.requestId) return;
        this.orders = Array.isArray(response) ? response : (response.results || []);
        this.count = Array.isArray(response) ? response.length : (response.count || 0);
      } catch (err) {
        if (requestId !== this.requestId) return;
        this.error = { status: (err && err.status) || null };
        this.orders = [];
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    async onExpand({ item, value }) {
      if (!value || this.details[item.id]) return;
      this.$set(this.detailsLoading, item.id, true);
      try {
        const detail = await api.get(`/service-catalog/purchases/${item.id}/`);
        this.$set(this.details, item.id, detail);
      } catch (e) {
        // Сообщение «не удалось загрузить» покажет раскрытие строки
      } finally {
        this.$set(this.detailsLoading, item.id, false);
      }
    },
    askAction(order, action) {
      this.confirmDialog = {
        show: true, action, order, saving: false, error: '',
      };
    },
    async runAction() {
      const { order, action } = this.confirmDialog;
      this.confirmDialog.saving = true;
      this.confirmDialog.error = '';
      try {
        await api.post(`/service-catalog/purchases/${order.id}/${action}/`, {});
        this.$toast.success(action === 'confirm' ? 'Заказ отмечен оплаченным' : 'Заказ отменён', '', { position: 'topRight' });
        this.confirmDialog.show = false;
        this.$delete(this.details, order.id);
        this.load();
        this.$emit('changed');
      } catch (err) {
        const data = (err && err.data) || {};
        this.confirmDialog.error = Object.values(data).flat().map(String).join(' ') || 'Не удалось изменить статус';
      } finally {
        this.confirmDialog.saving = false;
      }
    },
    formatDay(value) {
      return value ? moment(value).format('DD.MM.YYYY') : '—';
    },
    statusTitle(status) {
      return (STATUS[status] && STATUS[status].title) || status || '—';
    },
    statusColor(status) {
      return (STATUS[status] && STATUS[status].color) || 'grey';
    },
    roleTitle(role) {
      const type = role && typeof role === 'object' ? role.type : role;
      return type ? (ROLES[type] || type) : '—';
    },
    customerName(order) {
      return [order.user_last_name, order.user_first_name].filter(Boolean).join(' ') || order.user_email || '—';
    },
    itemsSummary(detail) {
      return (detail.items || []).map((i) => `${i.service_name} × ${i.quantity}`).join(', ') || '—';
    },
    resultText(detail) {
      if (detail.status === 'paid') {
        const paid = detail.paid_at ? ` ${moment(detail.paid_at).format('DD.MM.YYYY')}` : '';
        const toBuffer = (detail.items || []).some((i) => i.replenish_buffer);
        return toBuffer
          ? `Оплачен${paid}. Тесты из заказа зачисляются в буфер компании — остаток видно на «Обзоре».`
          : `Оплачен${paid}.`;
      }
      if (detail.status === 'pending') return 'Ожидает оплаты. С вами свяжется менеджер.';
      if (detail.status === 'cancelled') return 'Заказ отменён.';
      if (detail.status === 'refunded') return 'Оформлен возврат.';
      return '';
    },
  },
  created() {
    this.load();
  },
};
</script>
